import { useEffect, useRef, useState } from 'react';

import { CoverageHoursEntry } from './useCoverageHours';

export type UseCoverageHoursOptimisticRowsArgs = {
  entries: CoverageHoursEntry[];
  optimisticEntries: CoverageHoursEntry[];
  page: number;
  setPage: (page: number) => void;
  refreshCoverageHours: () => Promise<void>;
};

export type UseCoverageHoursOptimisticRowsResult = {
  displayedEntries: CoverageHoursEntry[];
  onOptimisticDelete: (routingId: string) => void;
  onDeleteError: (routingId: string) => void;
  onDeleteSuccess: () => Promise<void>;
};

export function useCoverageHoursOptimisticRows({
  entries,
  optimisticEntries,
  page,
  setPage,
  refreshCoverageHours,
}: UseCoverageHoursOptimisticRowsArgs): UseCoverageHoursOptimisticRowsResult {
  const [deletingRoutingIds, setDeletingRoutingIds] = useState<string[]>([]);

  // The server sorts by content (category, issue, team, time), not by
  // creation time, so a fresh optimistic row only belongs on page 0.
  const displayedEntries = (
    page === 0 ? [...optimisticEntries, ...entries] : entries
  ).filter(({ routingId }) => !deletingRoutingIds.includes(routingId));

  // Set when hiding the rows emptied a page past the first. The page change
  // refetches on its own, so the success path must not refetch the old offset
  // as well, and a failed delete has to land the user back where they were.
  const clampedFromPage = useRef<number | null>(null);

  const onOptimisticDelete = (routingId: string) => {
    setDeletingRoutingIds(prev => [...prev, routingId]);

    const remaining = entries.filter(
      entry =>
        entry.routingId !== routingId &&
        !deletingRoutingIds.includes(entry.routingId)
    );

    clampedFromPage.current = page > 0 && remaining.length === 0 ? page : null;

    if (clampedFromPage.current !== null) {
      setPage(page - 1);
    }
  };

  const onDeleteError = (routingId: string) => {
    setDeletingRoutingIds(prev => prev.filter(id => id !== routingId));

    if (clampedFromPage.current !== null) {
      setPage(clampedFromPage.current);
      clampedFromPage.current = null;
    }
  };

  const onDeleteSuccess = async () => {
    if (clampedFromPage.current !== null) {
      clampedFromPage.current = null;

      return;
    }

    await refreshCoverageHours();
  };

  // A pairing stays hidden until the rows it owned are gone from the server's
  // answer, so a deleted row never flashes back while the refetch is in flight.
  useEffect(() => {
    const served = new Set(entries.map(entry => entry.routingId));

    setDeletingRoutingIds(prev => {
      const next = prev.filter(routingId => served.has(routingId));

      return next.length === prev.length ? prev : next;
    });
  }, [entries]);

  return {
    displayedEntries,
    onOptimisticDelete,
    onDeleteError,
    onDeleteSuccess,
  };
}
